/**
 * SKOP Transaction Simulation
 *
 * Simulates swap transactions before execution to detect potential issues.
 * Free: 3/day, Pro/API: unlimited.
 */

import { getStoredToken } from './backend/client';

// API base URL
const API_BASE = import.meta.env.VITE_API_URL || 'https://api.skop.site';

// SOL mint address
const SOL_MINT = 'So11111111111111111111111111111111111111112';

export type RiskLevel = 'low' | 'medium' | 'high';

export interface SimulationRequest {
  tokenAddress: string;
  action: 'buy' | 'sell';
  amount: number;
  slippageBps: number;
  userWallet: string;
}

export interface SimulationFees {
  networkFee: number;      // SOL
  priorityFee: number;     // SOL
  skopFee: number;         // SOL
  platformFee: number;     // SOL (pump.fun/raydium etc)
  totalFees: number;       // SOL
}

export interface SimulationRisks {
  slippageRisk: RiskLevel;
  mevExposure: RiskLevel;
  sandwichRisk: RiskLevel;
  liquidityRisk: RiskLevel;
}

export interface SimulationResult {
  success: boolean;
  expectedOutput: string;
  minOutput: string;
  priceImpact: number;
  fees: SimulationFees;
  risks: SimulationRisks;
  warnings: string[];
  logs?: string[];
  computeUnits?: number;
  simulatedAt: number;
}

// Cache configuration
const SIMULATION_CACHE_KEY = 'skop_simulation_cache';
const SIMULATION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

interface CachedSimulation {
  result: SimulationResult;
  cachedAt: number;
}

// ============================================================================
// Simulation Functions
// ============================================================================

/**
 * Simulate a swap transaction
 *
 * For authenticated users, calls the real API.
 * Falls back to mock simulation for unauthenticated users (demo only).
 */
export async function simulateSwap(params: SimulationRequest): Promise<SimulationResult> {
  // Check cache first
  const cached = getCachedSimulation(params);
  if (cached) {
    return cached;
  }

  // Try to use real API if authenticated
  const token = getStoredToken();
  if (token) {
    try {
      const result = await callSimulationApi(params);
      cacheSimulation(params, result);
      return result;
    } catch (error) {
      // If 429 (rate limited), fall through to mock
      if (error instanceof Error && error.message.includes('limit reached')) {
        console.log('Simulation limit reached - using demo mode');
      } else {
        throw error;
      }
    }
  }

  // Fallback to mock simulation for demo
  const result = await generateMockSimulation(params);
  cacheSimulation(params, result);
  return result;
}

/**
 * Call the real simulation API
 */
async function callSimulationApi(params: SimulationRequest): Promise<SimulationResult> {
  const { tokenAddress, action, amount, slippageBps } = params;

  // Determine input/output mints based on action
  const inputMint = action === 'buy' ? SOL_MINT : tokenAddress;
  const outputMint = action === 'buy' ? tokenAddress : SOL_MINT;

  // Convert amount to lamports/tokens (assuming 9 decimals for SOL, 6 for token)
  const decimals = action === 'buy' ? 9 : 6;
  const amountInSmallestUnit = Math.floor(amount * Math.pow(10, decimals));

  const response = await fetch(`${API_BASE}/api/simulate/swap`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${getStoredToken()}`,
    },
    body: JSON.stringify({
      inputMint,
      outputMint,
      amount: amountInSmallestUnit,
      slippageBps,
    }),
  });

  const data = await response.json() as {
    success: boolean;
    error?: string;
    data?: {
      quote?: {
        inputAmount: string;
        outputAmount: string;
        priceImpact: number;
        minimumReceived: string;
        routes: Array<{ label: string; percent: number }>;
        fees: { platformFee: number; networkFee: number };
      };
      warnings: string[];
    };
  };

  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Simulation failed');
  }

  const quote = data.data?.quote;
  if (!quote) {
    throw new Error('No quote returned from simulation');
  }

  // Convert API response to SimulationResult format
  const priceImpact = quote.priceImpact;
  const warnings = data.data?.warnings || [];

  // Assess risks based on price impact and warnings
  const risks = assessRisksFromQuote(priceImpact, warnings);

  return {
    success: true,
    expectedOutput: quote.outputAmount,
    minOutput: quote.minimumReceived,
    priceImpact,
    fees: {
      networkFee: quote.fees.networkFee / 1e9,
      priorityFee: 0.0001,
      skopFee: 0,
      platformFee: quote.fees.platformFee / 1e9,
      totalFees: (quote.fees.networkFee + quote.fees.platformFee) / 1e9 + 0.0001,
    },
    risks,
    warnings,
    simulatedAt: Date.now(),
  };
}

/**
 * Assess risks from API quote response
 */
function assessRisksFromQuote(priceImpact: number, warnings: string[]): SimulationRisks {
  const slippageRisk: RiskLevel = priceImpact > 5 ? 'high' : priceImpact > 2 ? 'medium' : 'low';

  const hasLiquidityWarning = warnings.some(w =>
    w.toLowerCase().includes('liquidity') || w.toLowerCase().includes('route splits')
  );
  const liquidityRisk: RiskLevel = hasLiquidityWarning ? 'medium' : 'low';

  // MEV risk based on price impact
  const mevExposure: RiskLevel = priceImpact > 3 ? 'high' : priceImpact > 1 ? 'medium' : 'low';
  const sandwichRisk: RiskLevel = priceImpact > 2 && mevExposure !== 'low' ? 'high' :
    priceImpact > 1 ? 'medium' : 'low';

  return {
    slippageRisk,
    mevExposure,
    sandwichRisk,
    liquidityRisk,
  };
}

/**
 * Generate mock simulation result based on trade parameters
 */
async function generateMockSimulation(params: SimulationRequest): Promise<SimulationResult> {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 800));

  const { amount, slippageBps, action } = params;

  // Calculate mock values
  const priceImpact = calculateMockPriceImpact(amount, action);
  const expectedOutput = action === 'buy'
    ? (amount * 1000000 * (1 - priceImpact / 100)).toFixed(0)
    : (amount / 1000000 * (1 - priceImpact / 100)).toFixed(6);

  const minOutput = action === 'buy'
    ? (parseFloat(expectedOutput) * (1 - slippageBps / 10000)).toFixed(0)
    : (parseFloat(expectedOutput) * (1 - slippageBps / 10000)).toFixed(6);

  // Calculate fees
  const fees = calculateMockFees(amount, action);

  // Assess risks
  const risks = assessMockRisks(priceImpact, amount);

  // Generate warnings
  const warnings = generateWarnings(priceImpact, risks, fees);

  // Determine success
  const success = priceImpact < 10 && risks.liquidityRisk !== 'high';

  return {
    success,
    expectedOutput,
    minOutput,
    priceImpact,
    fees,
    risks,
    warnings,
    computeUnits: 150000 + Math.floor(Math.random() * 50000),
    simulatedAt: Date.now(),
  };
}

/**
 * Calculate mock price impact based on trade size
 */
function calculateMockPriceImpact(amount: number, action: 'buy' | 'sell'): number {
  // Larger trades have higher impact
  const baseImpact = action === 'buy' ? 0.5 : 0.7;
  const sizeMultiplier = Math.log10(amount * 100 + 1);
  return Math.min(baseImpact * sizeMultiplier, 15);
}

/**
 * Calculate mock fee breakdown
 */
function calculateMockFees(amount: number, action: 'buy' | 'sell'): SimulationFees {
  const networkFee = 0.000005; // Base transaction fee
  const priorityFee = 0.0001;  // Priority fee
  const skopFee = action === 'buy' ? amount * 0.001 : 0; // 0.1% on buys (for non-whale)
  const platformFee = amount * 0.01; // 1% platform fee

  return {
    networkFee,
    priorityFee,
    skopFee,
    platformFee,
    totalFees: networkFee + priorityFee + skopFee + platformFee,
  };
}

/**
 * Assess transaction risks
 */
function assessMockRisks(priceImpact: number, amount: number): SimulationRisks {
  // Slippage risk based on price impact
  const slippageRisk: RiskLevel = priceImpact > 5 ? 'high' : priceImpact > 2 ? 'medium' : 'low';

  // MEV exposure based on trade size and randomness
  const mevRandom = Math.random();
  const mevExposure: RiskLevel = amount > 5 && mevRandom > 0.7 ? 'high' :
    amount > 1 && mevRandom > 0.5 ? 'medium' : 'low';

  // Sandwich risk based on price impact and MEV exposure
  const sandwichRisk: RiskLevel = priceImpact > 3 && mevExposure !== 'low' ? 'high' :
    priceImpact > 1.5 ? 'medium' : 'low';

  // Liquidity risk (mock based on randomness for demo)
  const liquidityRandom = Math.random();
  const liquidityRisk: RiskLevel = liquidityRandom > 0.9 ? 'high' :
    liquidityRandom > 0.7 ? 'medium' : 'low';

  return {
    slippageRisk,
    mevExposure,
    sandwichRisk,
    liquidityRisk,
  };
}

/**
 * Generate warning messages based on simulation results
 */
function generateWarnings(
  priceImpact: number,
  risks: SimulationRisks,
  fees: SimulationFees
): string[] {
  const warnings: string[] = [];

  // Price impact warnings
  if (priceImpact > 5) {
    warnings.push(`High price impact (${priceImpact.toFixed(1)}%) - consider reducing trade size`);
  } else if (priceImpact > 2) {
    warnings.push(`Moderate price impact (${priceImpact.toFixed(1)}%)`);
  }

  // Risk warnings
  if (risks.mevExposure === 'high') {
    warnings.push('High MEV exposure - bots may front-run this trade');
  }
  if (risks.sandwichRisk === 'high') {
    warnings.push('Sandwich attack risk detected - consider using private RPC');
  }
  if (risks.liquidityRisk === 'high') {
    warnings.push('Low liquidity - execution may fail or have high slippage');
  }

  // Fee warnings
  if (fees.totalFees > 0.1) {
    warnings.push(`High total fees: ${fees.totalFees.toFixed(4)} SOL`);
  }

  return warnings;
}

// ============================================================================
// Cache Functions
// ============================================================================

/**
 * Get cache key for simulation
 */
function getSimulationCacheKey(params: SimulationRequest): string {
  return `${params.tokenAddress}_${params.action}_${params.amount}_${params.slippageBps}`;
}

/**
 * Get cached simulation if valid
 */
function getCachedSimulation(params: SimulationRequest): SimulationResult | null {
  try {
    const key = getSimulationCacheKey(params);
    const stored = localStorage.getItem(`${SIMULATION_CACHE_KEY}_${key}`);
    if (!stored) return null;

    const cached: CachedSimulation = JSON.parse(stored);
    if (Date.now() - cached.cachedAt > SIMULATION_CACHE_DURATION) {
      localStorage.removeItem(`${SIMULATION_CACHE_KEY}_${key}`);
      return null;
    }

    return cached.result;
  } catch {
    return null;
  }
}

/**
 * Cache simulation result
 */
function cacheSimulation(params: SimulationRequest, result: SimulationResult): void {
  try {
    const key = getSimulationCacheKey(params);
    const cached: CachedSimulation = {
      result,
      cachedAt: Date.now(),
    };
    localStorage.setItem(`${SIMULATION_CACHE_KEY}_${key}`, JSON.stringify(cached));
  } catch {
    // Ignore cache errors
  }
}

/**
 * Clear simulation cache
 */
export function clearSimulationCache(): void {
  try {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(SIMULATION_CACHE_KEY));
    keys.forEach(k => localStorage.removeItem(k));
  } catch {
    // Ignore errors
  }
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get color for risk level
 */
export function getRiskColor(level: RiskLevel): string {
  switch (level) {
    case 'high': return '#ff3b3b';
    case 'medium': return '#ffb800';
    case 'low': return '#00ff88';
  }
}

/**
 * Get label for risk level
 */
export function getRiskLabel(level: RiskLevel): string {
  switch (level) {
    case 'high': return 'High';
    case 'medium': return 'Medium';
    case 'low': return 'Low';
  }
}

/**
 * Format fee amount
 */
export function formatFee(amount: number): string {
  if (amount < 0.0001) return '< 0.0001 SOL';
  return `${amount.toFixed(4)} SOL`;
}
